import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { enums } from '@kattana-io/common';
import useTickerLivePrice from '../../components/Watchlist/hooks/useTickerLivePrice';
import useTicker from '../../hooks/useTicker';
import { IStore } from '../../reducers';
import { ICurrentState } from '../../reducers/current';
import PairIcons from '../PairIcons';
import NumberDisplay from '../NumberDisplay';
import { getPriceChange } from '../../helpers/priceChange';
import PoolInfo from '../../components/Pool/PoolInfo';
import useTickerPool from '../../components/Pool/useTickerPool';
import { onCopyAddress, onWalletScan } from '../../helpers/addressUtils';
import Text from '../../atoms/Text';

import './styles.scss';

const CEX_POOL_HEIGHT = 120;
const DEX_POOL_HEIGHT = 240;

interface ITickerCardProps {
  current: ICurrentState;
}
/**
 * A mobile view card which displays info about pair
 * @param props
 * @returns
 */
const TickerCard = ({ current }: ITickerCardProps) => {
  const ticker = useTicker(current);
  const { price } = useTickerLivePrice(current);

  const { priceChange, priceChangePercent } = getPriceChange(
    price.rawPrice,
    current
  );

  const { loading, rows, extraRows, poolInfo } = useTickerPool(current, ticker);
  const subName = priceChangePercent > 0 ? 'green' : 'red';

  const { name, base_address, quote_address, chain, base_slug, quote_slug } =
    ticker;

  return (
    <div className='ticker__card'>
      <div className='ticker__card__hd'>
        <div className='ticker__card__hd__left'>
          <div className='ticker__card__hd__left__ticker_group'>
            <PairIcons
              chain={chain}
              pair={name}
              base_address={base_address}
              quote_address={quote_address}
              base_slug={base_slug}
              quote_slug={quote_slug}
            />
            {ticker.isDEX ? (
              <Text size={16}>
                <Link to={`/en/token/${current.chain}/${base_address}`}>
                  <Text size={16} className='text--grey-200 text--weight-500'>
                    {ticker.base}
                  </Text>
                </Link>
                <Text size={16} className='text--grey-200 text--weight-500'>
                  /
                </Text>
                <Link to={`/en/token/${current.chain}/${quote_address}`}>
                  <Text size={16} className='text--grey-200 text--weight-500'>
                    {ticker.quote}
                  </Text>
                </Link>
              </Text>
            ) : (
              <Text size={16} className='text--grey-200 text--weight-500'>
                {name}
              </Text>
            )}
          </div>
          <div className='ticker__card__hd__left__action_group'>
            {ticker.isDEX && (
              <>
                <div
                  className='pool_row__coin__action icon-copy'
                  onClick={() => onCopyAddress(current.address)}
                />
                <div
                  className='pool_row__coin__action icon-top-right-square'
                  onClick={() => onWalletScan(current.address, current.chain)}
                />
              </>
            )}
            {!ticker.isDEX && poolInfo.links?.website && (
              <a
                href={poolInfo.links.website}
                className='pool_row__coin__action icon-top-right-square'
                target='_blank'
                rel='noreferrer'
              />
            )}
          </div>
        </div>
        <div className='ticker__card__hd__right'>
          <span>{price.formattedPrice}</span>
          {priceChangePercent !== 0 && (
            <div
              className={`ticker__card__hd__right__sub ticker__card__hd__right__sub--${subName}`}
            >
              <NumberDisplay
                value={priceChangePercent}
                round={2}
                startSymbol='('
                endSymbol='%)'
                string
              />
              &nbsp;
              <NumberDisplay
                value={priceChange}
                round={2}
                startSymbol='$'
                string
              />
            </div>
          )}
        </div>
      </div>
      <div
        className='ticker__card__info'
        style={{
          height:
            current.kind === enums.ETickerKind.CEX
              ? CEX_POOL_HEIGHT
              : DEX_POOL_HEIGHT,
        }}
      >
        <PoolInfo
          current={current}
          loading={loading}
          rows={rows.slice(2)}
          securityRows={extraRows}
          sameBaseTickers={[]}
          mobile
        />
      </div>
    </div>
  );
};

const CurrentTickerCard = () => {
  const ticker = useSelector((state: IStore) => state.current);
  return <TickerCard current={ticker} />;
};

export { CurrentTickerCard, TickerCard };
